import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView, Image, Platform } from 'react-native';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';

const Toolbar: React.FC<{ editor: any }> = ({ editor }) => {
  if (!editor) return null;
  
  const extractYouTubeId = (url: string) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const handleInsertImage = () => {
    const url = window.prompt('图片地址（URL）');
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };
  
  const handleInsertVideo = () => {
    const url = window.prompt('YouTube视频地址');
    if (url) {
      const videoId = extractYouTubeId(url);
      if (videoId) {
        editor.chain().focus().setYoutubeVideo({ 
          src: `https://www.youtube.com/embed/${videoId}` 
        }).run();
      } else {
        alert('请输入有效的 YouTube 视频链接');
      }
    }
  };
  
  const handleInsertPDF = () => {
    const url = window.prompt('PDF文件地址');
    if (url) editor.chain().focus().insertContent(`<div style='border:1px solid #e5e7eb;padding:12px;background:#fafbfc;border-radius:8px;'>PDF预览占位：<a href='${url}' target='_blank'>点击查看PDF</a></div>`).run();
  };
  
  const handleInsertQR = () => {
    const text = window.prompt('请输入要生成二维码的内容');
    if (text) {
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(text)}`;
      editor.chain().focus().setImage({ src: qrUrl }).run();
    }
  };

  return (
    <View style={toolbarStyles.container}>
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
      >
        <Text style={toolbarStyles.buttonText}>B</Text>
      </TouchableOpacity>
      
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
      >
        <Text style={[toolbarStyles.buttonText, { fontStyle: 'italic' }]}>I</Text>
      </TouchableOpacity>
      
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().setHeading({ level: 1 }).run()}
        disabled={!editor.can().chain().focus().setHeading({ level: 1 }).run()}
      >
        <Text style={toolbarStyles.buttonText}>H1</Text>
      </TouchableOpacity>
      
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().setHeading({ level: 2 }).run()}
        disabled={!editor.can().chain().focus().setHeading({ level: 2 }).run()}
      >
        <Text style={toolbarStyles.buttonText}>H2</Text>
      </TouchableOpacity>
      
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().toggleBulletList().run()}
        disabled={!editor.can().chain().focus().toggleBulletList().run()}
      >
        <Text style={toolbarStyles.buttonText}>• List</Text>
      </TouchableOpacity>
      
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().toggleOrderedList().run()}
        disabled={!editor.can().chain().focus().toggleOrderedList().run()}
      >
        <Text style={toolbarStyles.buttonText}>1. List</Text>
      </TouchableOpacity>
      
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().chain().focus().undo().run()}
      >
        <Text style={toolbarStyles.buttonText}>↺</Text>
      </TouchableOpacity>
      
      <TouchableOpacity 
        style={toolbarStyles.button}
        onPress={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().chain().focus().redo().run()}
      >
        <Text style={toolbarStyles.buttonText}>↻</Text>
      </TouchableOpacity>
      
      <TouchableOpacity style={toolbarStyles.button} onPress={handleInsertImage}>
        <Text style={toolbarStyles.buttonText}>图片</Text>
      </TouchableOpacity>
      
      <TouchableOpacity style={toolbarStyles.button} onPress={handleInsertVideo}>
        <Text style={toolbarStyles.buttonText}>视频</Text>
      </TouchableOpacity>
      
      <TouchableOpacity style={toolbarStyles.button} onPress={handleInsertPDF}>
        <Text style={toolbarStyles.buttonText}>PDF</Text>
      </TouchableOpacity>
      
      <TouchableOpacity style={toolbarStyles.button} onPress={handleInsertQR}>
        <Text style={toolbarStyles.buttonText}>QR</Text>
      </TouchableOpacity>
    </View>
  );
};

const toolbarStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
    justifyContent: 'flex-start',
    width: '100%',
  },
  button: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f5f5f5',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    minWidth: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
});

const CustomEditorEmptyModal: React.FC = () => {
  const editor = useEditor({
    extensions: [StarterKit, ImageExtension, Youtube],
    content: '',
  });
  
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const cities = ['西安', '上海', '香港', '北京', '杭州', '广州', '成都', '南京'];
  const tags = ['美食', '摄影', '徒步', '亲子', '情侣', '穷游', '自驾', '海岛', '城市漫步', '历史人文'];

  const handleTagPress = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handlePublish = () => {
    if (!title.trim()) {
      alert('请输入游记标题');
      return;
    }
    if (!selectedCity) {
      alert('请选择目的地');
      return;
    }
    
    const content = editor?.getHTML() || '';
    if (!content.trim() || content === '<p></p>') {
      alert('请输入游记内容');
      return;
    }

    // 这里处理发布逻辑
    console.log('发布游记:', {
      title,
      summary,
      city: selectedCity,
      coverImage,
      tags: selectedTags,
      content
    });
    
    alert('游记发布成功！');
  };

  const handleImageUpload = () => {
    // 模拟图片上传
    const url = window.prompt('请输入图片URL（模拟上传）');
    if (url) {
      setCoverImage(url);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* 封面图区域 */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>封面图片</Text>
        {coverImage ? (
          <View style={styles.coverImageContainer}>
            <Image source={{ uri: coverImage }} style={styles.coverImage} />
            <TouchableOpacity style={styles.changeImageButton} onPress={handleImageUpload}>
              <Text style={styles.changeImageText}>更换图片</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <TouchableOpacity style={styles.uploadArea} onPress={handleImageUpload}>
            <Text style={styles.uploadText}>+ 上传封面图</Text>
            <Text style={styles.uploadHint}>建议尺寸 16:9</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* 标题输入 */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>游记标题</Text>
        <TextInput
          style={styles.titleInput}
          placeholder="给你的游记起个吸引人的标题..."
          placeholderTextColor="#999"
          value={title}
          onChangeText={setTitle}
          maxLength={100}
        />
        <Text style={styles.charCount}>{title.length}/100</Text>
      </View>

      {/* 摘要简介 */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>简短摘要</Text>
        <TextInput
          style={styles.summaryInput}
          placeholder="用一两句话描述这次旅行的亮点..."
          placeholderTextColor="#999"
          value={summary}
          onChangeText={setSummary}
          multiline
          numberOfLines={3}
          textAlignVertical="top"
          maxLength={200}
        />
        <Text style={styles.charCount}>{summary.length}/200</Text>
      </View>

      {/* 城市选择 */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>目的地</Text>
        <View style={styles.cityButtons}>
          {cities.map(city => (
            <TouchableOpacity
              key={city}
              style={[
                styles.cityButton,
                selectedCity === city && styles.cityButtonSelected
              ]}
              onPress={() => setSelectedCity(city)}
            >
              <Text style={[
                styles.cityButtonText,
                selectedCity === city && styles.cityButtonTextSelected
              ]}>
                {city}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* 标签选择 */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>添加标签</Text>
        <View style={styles.tagContainer}>
          {tags.map(tag => (
            <TouchableOpacity 
              key={tag} 
              style={[
                styles.tag,
                selectedTags.includes(tag) && styles.tagSelected
              ]}
              onPress={() => handleTagPress(tag)}
            >
              <Text style={[
                styles.tagText,
                selectedTags.includes(tag) && styles.tagTextSelected
              ]}>
                #{tag}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {selectedTags.length > 0 && (
          <Text style={styles.selectedTagsHint}>已选择: {selectedTags.join(', ')}</Text>
        )}
      </View>

      {/* 富文本编辑器区域 */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>游记内容</Text>
        <View style={styles.editorContainer}>
          <Toolbar editor={editor} />
          <EditorContent
            editor={editor}
            style={styles.editorContent}
          />
        </View>
      </View>

      {/* 发布按钮 */}
      <TouchableOpacity style={styles.publishButton} onPress={handlePublish}>
        <Text style={styles.publishText}>发布游记</Text>
      </TouchableOpacity>

      {/* TipTap 样式 */}
      <style>{`
        .tiptap iframe {
          display: block;
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          aspect-ratio: 16/9 !important;
          border-radius: 8px;
          margin: 8px 0;
          background: #000;
          box-sizing: border-box;
        }
        .tiptap img {
          max-width: 100%;
          width: 100%;
          height: auto;
          display: block;
          margin: 8px 0;
          border-radius: 8px;
        }
        .tiptap {
          width: 100%;
          max-width: 100%;
          overflow-wrap: break-word;
          min-height: 200px;
          padding: 12px;
          font-size: 16px;
          line-height: 1.6;
        }
        .tiptap h1 {
          font-size: 24px;
          font-weight: bold;
          margin: 16px 0 8px 0;
        }
        .tiptap h2 {
          font-size: 20px;
          font-weight: bold;
          margin: 12px 0 6px 0;
        }
        .tiptap ul, .tiptap ol {
          padding-left: 20px;
          margin: 8px 0;
        }
        .tiptap li {
          margin: 4px 0;
        }
      `}</style>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  contentContainer: {
    paddingBottom: 40,
  },
  section: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 16,
    marginTop: 16,
    ...Platform.select({
      web: {
        boxShadow: '0 1px 3px rgba(0,0,0,0.10)'
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 3,
        elevation: 2,
      }
    }),
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
    color: '#333',
  },
  // 封面图样式
  uploadArea: {
    width: '100%',
    height: 120,
    borderWidth: 2,
    borderColor: '#ddd',
    borderStyle: 'dashed',
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fafafa',
  },
  uploadText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
  uploadHint: {
    fontSize: 12,
    color: '#999',
    marginTop: 4,
  },
  coverImageContainer: {
    alignItems: 'center',
  },
  coverImage: {
    width: '100%',
    height: 200,
    borderRadius: 8,
    marginBottom: 8,
  },
  changeImageButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f0f0f0',
    borderRadius: 6,
  },
  changeImageText: {
    fontSize: 14,
    color: '#666',
  },
  // 输入框样式
  titleInput: {
    fontSize: 16,
    padding: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fafafa',
  },
  summaryInput: {
    fontSize: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fafafa',
    height: 80,
    textAlignVertical: 'top',
  },
  charCount: {
    fontSize: 12,
    color: '#999',
    textAlign: 'right',
    marginTop: 4,
  },
  // 城市选择样式
  cityButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  cityButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
  },
  cityButtonSelected: {
    backgroundColor: '#e3f2fd',
    borderColor: '#2196f3',
  },
  cityButtonText: {
    fontSize: 14,
    color: '#666',
  },
  cityButtonTextSelected: {
    color: '#2196f3',
    fontWeight: '500',
  },
  // 标签样式
  tagContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#f8f9fa',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  tagSelected: {
    backgroundColor: '#fff3cd',
    borderColor: '#ffc107',
  },
  tagText: {
    fontSize: 12,
    color: '#6c757d',
  },
  tagTextSelected: {
    color: '#856404',
    fontWeight: '500',
  },
  selectedTagsHint: {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
    fontStyle: 'italic',
  },
  // 编辑器样式
  editorContainer: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    overflow: 'hidden',
  },
  editorContent: {
    minHeight: 200,
  },
  // 发布按钮样式
  publishButton: {
    backgroundColor: '#ff6b35',
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    margin: 16,
    marginTop: 24,
    ...Platform.select({
      web: {
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      }
    }),
  },
  publishText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CustomEditorEmptyModal;